import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ArcElement, Chart, Legend, PieController, Tooltip, type Plugin } from 'chart.js';
import { forkJoin } from 'rxjs';

import { BusinessUnitsService } from '@services/business-units.service';
import { JobsService } from '@services/jobs.service';
import { ProjectsService } from '@services/projects.service';
import { BusinessUnitSummary } from '@models/business-unit.interface';
import { LookupDto } from '@models/lookup-dto.interface';
import { TestMetric, TestMetricGroup, TestMetrics } from '@models/test-metrics.interface';

Chart.register(ArcElement, Legend, PieController, Tooltip);

const metricValueLabelsPlugin: Plugin<'pie'> = {
  id: 'metricValueLabels',
  afterDatasetsDraw: chart => {
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);
    const context = chart.ctx;
    const total = (dataset?.data ?? []).reduce((sum, value) => sum + Number(value ?? 0), 0);

    context.save();
    context.fillStyle = '#ffffff';
    context.font = '600 13px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    meta.data.forEach((element, index) => {
      const arc = element as ArcElement;
      const value = Number(dataset?.data[index] ?? 0);
      if (!value) return;
      const percentage = total > 0 ? (value / total) * 100 : 0;

      const angle = (arc.startAngle + arc.endAngle) / 2;
      const radius = arc.innerRadius + (arc.outerRadius - arc.innerRadius) * 0.58;
      const x = arc.x + Math.cos(angle) * radius;
      const y = arc.y + Math.sin(angle) * radius;
      context.fillText(`${value.toLocaleString()} (${percentage.toFixed(1)}%)`, x, y);
    });

    context.restore();
  },
};

@Component({
  selector: 'app-metrics-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule],
  templateUrl: './metrics-page.component.html',
  styleUrl: './metrics-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricsPageComponent implements AfterViewInit, OnDestroy, OnInit {
  private readonly businessUnitsService = inject(BusinessUnitsService);
  private readonly jobsService = inject(JobsService);
  private readonly projectsService = inject(ProjectsService);
  private readonly fb = inject(FormBuilder);

  protected readonly businessUnits = signal<BusinessUnitSummary[]>([]);
  protected readonly assetClasses = signal<LookupDto[]>([]);
  protected readonly metrics = signal<TestMetric[]>([]);
  protected readonly totalCount = signal(0);
  protected readonly businessUnitGroups = signal<TestMetricGroup[]>([]);
  protected readonly jobTypeGroups = signal<TestMetricGroup[]>([]);
  protected readonly assetClassGroups = signal<TestMetricGroup[]>([]);
  protected readonly jobRequestTypeGroups = signal<TestMetricGroup[]>([]);
  protected readonly isLoadingLookups = signal(true);
  protected readonly isRefreshing = signal(false);
  protected readonly errorMessage = signal('');

  @ViewChild('businessUnitChart') private businessUnitChartCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('jobTypeChart') private jobTypeChartCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('assetClassChart') private assetClassChartCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('jobRequestTypeChart') private jobRequestTypeChartCanvas?: ElementRef<HTMLCanvasElement>;

  private businessUnitChart?: Chart<'pie'>;
  private jobTypeChart?: Chart<'pie'>;
  private assetClassChart?: Chart<'pie'>;
  private jobRequestTypeChart?: Chart<'pie'>;
  private latestMetrics?: TestMetrics;

  protected readonly filterForm = this.fb.nonNullable.group({
    fromDate: [this.getStartOfYear(), Validators.required],
    toDate: [this.getToday(), Validators.required],
    businessUnitId: [null as number | null],
    assetClassId: [null as number | null],
  });

  ngOnInit(): void {
    this.loadLookups();
    this.refreshMetrics();
  }

  protected refreshMetrics(): void {
    this.errorMessage.set('');
    this.filterForm.markAllAsTouched();

    const { fromDate, toDate } = this.filterForm.getRawValue();
    if (!fromDate || !toDate || fromDate > toDate) {
      this.errorMessage.set('From date must be on or before To date.');
      return;
    }

    this.isRefreshing.set(true);
    const values = this.filterForm.getRawValue();
    this.jobsService.getTestMetrics({
      fromDate: values.fromDate,
      toDate: values.toDate,
      businessUnitId: values.businessUnitId,
      assetClassId: values.assetClassId,
    }).subscribe({
      next: result => {
        const normalizedResult: TestMetrics = {
          ...result,
          businessUnits: result.businessUnits ?? [],
          jobTypes: result.jobTypes ?? [],
          assetClasses: result.assetClasses ?? [],
          jobRequestTypes: result.jobRequestTypes ?? [],
        };
        this.metrics.set(result.items);
        this.totalCount.set(result.totalCount);
        this.businessUnitGroups.set(normalizedResult.businessUnits);
        this.jobTypeGroups.set(normalizedResult.jobTypes);
        this.assetClassGroups.set(normalizedResult.assetClasses);
        this.jobRequestTypeGroups.set(normalizedResult.jobRequestTypes);
        this.latestMetrics = normalizedResult;
        this.renderCharts(normalizedResult);
        this.isRefreshing.set(false);
      },
      error: () => {
        this.metrics.set([]);
        this.totalCount.set(0);
        this.clearChartGroups();
        this.latestMetrics = undefined;
        this.destroyCharts();
        this.errorMessage.set('Unable to load metrics. Please try again.');
        this.isRefreshing.set(false);
      },
    });
  }

  protected displayName(metric: TestMetric): string {
    return metric.name || metric.alias || 'Unknown';
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  ngAfterViewInit(): void {
    if (this.latestMetrics) {
      this.renderCharts(this.latestMetrics);
    }
  }

  private renderCharts(result: TestMetrics): void {
    this.destroyCharts();

    if (!this.businessUnitChartCanvas || !this.jobTypeChartCanvas
      || !this.assetClassChartCanvas || !this.jobRequestTypeChartCanvas) {
      return;
    }

    this.businessUnitChart = this.createPieChart(
      this.businessUnitChartCanvas.nativeElement,
      result.businessUnits,
    );
    this.jobTypeChart = this.createPieChart(
      this.jobTypeChartCanvas.nativeElement,
      result.jobTypes,
    );
    this.assetClassChart = this.createPieChart(
      this.assetClassChartCanvas.nativeElement,
      result.assetClasses,
    );
    this.jobRequestTypeChart = this.createPieChart(
      this.jobRequestTypeChartCanvas.nativeElement,
      result.jobRequestTypes,
    );
  }

  private createPieChart(canvas: HTMLCanvasElement, groups: TestMetricGroup[]): Chart<'pie'> {
    return new Chart(canvas, {
      type: 'pie',
      data: {
        labels: groups.map(group => group.name),
        datasets: [{
          data: groups.map(group => group.count),
          backgroundColor: [
            '#1f6f8b', '#f4a261', '#2a9d8f', '#e76f51',
            '#6c5ce7', '#e9c46a', '#264653', '#8ab17d',
          ],
          borderColor: '#ffffff',
          borderWidth: 2,
        }],
      },
      plugins: [metricValueLabelsPlugin],
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 16,
              usePointStyle: true,
            },
          },
        },
      },
    });
  }

  private clearChartGroups(): void {
    this.businessUnitGroups.set([]);
    this.jobTypeGroups.set([]);
    this.assetClassGroups.set([]);
    this.jobRequestTypeGroups.set([]);
  }

  private destroyCharts(): void {
    this.businessUnitChart?.destroy();
    this.jobTypeChart?.destroy();
    this.assetClassChart?.destroy();
    this.jobRequestTypeChart?.destroy();
    this.businessUnitChart = undefined;
    this.jobTypeChart = undefined;
    this.assetClassChart = undefined;
    this.jobRequestTypeChart = undefined;
  }

  private loadLookups(): void {
    this.isLoadingLookups.set(true);
    forkJoin({
      businessUnits: this.businessUnitsService.getList(),
      assetClasses: this.projectsService.getAssetClasses(),
    }).subscribe({
      next: lookups => {
        this.businessUnits.set(lookups.businessUnits);
        this.assetClasses.set(lookups.assetClasses);
        this.isLoadingLookups.set(false);
      },
      error: () => {
        this.errorMessage.set('Unable to load metric filters.');
        this.isLoadingLookups.set(false);
      },
    });
  }

  private getToday(): string {
    return this.toDateInputValue(new Date());
  }

  private getStartOfYear(): string {
    const today = new Date();
    return `${today.getFullYear()}-01-01`;
  }

  private toDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
